import { createContext, useContext, useState, useEffect, useCallback } from "react";

export type Language = "en" | "ar";

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  toggle: () => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const translations: Record<string, Record<string, string>> = {
  en: {
    // Nav
    home: "Home",
    about: "About Us",
    capabilities: "Capabilities",
    products: "Products & Solutions",
    cnc: "CNC & Fabrication",
    steel: "Steel Fabrication",
    coating: "Coating",
    catalogs: "Catalogs & Downloads",
    gallery: "Gallery",
    contact: "Contact Us",
    sendInquiry: "Send Inquiry",
    whatsapp: "WhatsApp",
    whatsappUs: "WhatsApp Us",

    // Hero
    heroLocation: "Riyadh, Saudi Arabia",
    heroTitleLine1: "Industrial",
    heroTitleLine2: "Supply &",
    heroTitleLine3: "Fabrication",
    heroSubtitle: "CNC laser cutting, steel fabrication, press brake bending, tube fabrication, powder coating, and project supply from Riyadh.",
    exploreCapabilities: "Explore Capabilities",
    viewCategories: "View Product Categories",
    whatsappInquiry: "WhatsApp Inquiry",

    // Stats
    coreServices: "Core Services",
    dayWorkWeek: "Day Work Week",
    projectFocused: "Project Focused",
    riyadhBase: "Riyadh Base",
    serviceAreas: "Service Areas",

    // About section
    aboutOverline: "ABOUT US",
    aboutTitle: "A Practical Industrial Partner for Your Projects",
    aboutP1: "Sky Mark Co. is a Riyadh-based company serving contractors, infrastructure projects, and industrial clients across Saudi Arabia with supply, CNC fabrication, steel fabrication, and coating solutions.",
    industrialSupply: "Industrial Supply",
    industrialSupplyShort: "Electrical, mechanical, civil",
    cncFab: "CNC Fabrication",
    cncFabShort: "Laser, bending, tube, welding",
    projectReady: "Project Ready",
    boqDrawing: "BOQ & Drawing Based",
    learnMore: "Learn More About Us",

    // Services
    whatWeDo: "WHAT WE DO",
    coreServicesTitle: "Core Services",

    // Products
    productScope: "PRODUCT SCOPE",
    productCategoriesTitle: "Product Categories & Fabrication Scope",
    viewAll: "View All Categories",

    // Why Choose
    whySkyMark: "WHY SKY MARK CO.",
    builtForProjects: "Built for Projects.",
    trustedByIndustry: "Trusted by Industry.",
    whyDescription: "We combine industrial supply, CNC fabrication, steel fabrication, and coating under one contact point — reducing coordination overhead for project teams.",

    // Process
    howWeWork: "HOW WE WORK",
    fromInquiryToDelivery: "From Inquiry to Delivery",
    step1Title: "Share Requirement",
    step1Desc: "BOQ, drawings, sketches, or material list",
    step2Title: "Review & Clarify",
    step2Desc: "Scope, material, finish, quantity, timeline",
    step3Title: "Quotation",
    step3Desc: "Offer + coordination of supply & delivery",
    step4Title: "Production",
    step4Desc: "Cutting, bending, fabrication, coating",
    step5Title: "Inspection & Delivery",
    step5Desc: "Checked, packed, delivered to project",

    // CTA
    readyToDiscuss: "Ready to Discuss Your Requirement?",
    ctaDescription: "Contact Sky Mark Co. for industrial supply, CNC fabrication, steel fabrication, powder coating, epoxy coating, and project-related material requirements.",
    callWhatsapp: "Call / WhatsApp",
    viewLocation: "View Location",

    // Trust strip
    riyadhBased: "Riyadh-Based",
    riyadhBasedDesc: "Local communication & quick coordination",
    multiScope: "Multi-Scope",
    multiScopeDesc: "Electrical, mechanical, civil & more",
    fabrication: "Fabrication",
    fabricationDesc: "CNC laser, bending, welding, tube",
    directContact: "Direct Contact",
    directContactDesc: "WhatsApp, mobile, email & inquiry form",

    // Service short descriptions
    electricalSupplyShort: "Electrical accessories & install",
    mechanicalSupplyShort: "Supports & accessories",
    civilMaterialsDesc: "Construction & infrastructure",
    steelFabDesc: "Custom assemblies",

    // Service short descriptions

    // About page
    aboutPageTitle: "About Sky Mark Co.",
    aboutPageSubtitle: "Riyadh-based industrial supply, fabrication, and coating solutions for project requirements across Saudi Arabia.",
    ourCompany: "OUR COMPANY",
    precisionPerformancePromise: "Precision. Performance. Promise.",
    whatWeDoCard: "What We Do",
    whatWeDoCardDesc: "Industrial trading, CNC fabrication, steel fabrication, and coating solutions for contractors and project teams across Saudi Arabia.",
    howWeWorkCard: "How We Work",
    howWeWorkCardDesc: "Understand the requirement. Coordinate the right solution. Support until fulfilled.",
    ourValues: "OUR VALUES",
    threePrinciples: "Three Principles That Guide Us",
    precision: "Precision",
    precisionDesc: "Clear understanding. Accurate output.",
    performance: "Performance",
    performanceDesc: "Responsive. Dependable. On time.",
    promise: "Promise",
    promiseDesc: "Reliable. Transparent. Committed.",
    whatWeSupport: "What We Support",
    ourMarketFocus: "Our Market Focus",
    infraConstruction: "Infrastructure & Construction",
    infraConstructionDesc: "Utilities, commercial developments, industrial facilities",
    contractorLed: "Contractor-Led Projects",
    contractorLedDesc: "EPC teams, procurement departments, project engineers",
    materialPackages: "Project-Based Material Packages",
    materialPackagesDesc: "BOQ-based and drawing-based supply coordination",
    workWithUs: "Work with Sky Mark Co.",
    workWithUsDesc: "Contact our team to discuss your industrial supply, fabrication, or coating requirement.",

    // Capabilities page
    capabilitiesTitle: "Capabilities",
    capabilitiesSubtitle: "Industrial supply, CNC fabrication, steel fabrication, coating, and project support capabilities from Riyadh, Saudi Arabia.",
    industrialSupplyCap: "Industrial Supply",
    industrialSupplyCapDesc: "Materials for electrical, mechanical, civil & general industrial scopes.",
    cncLaserCutting: "CNC Laser Cutting",
    cncLaserCuttingDesc: "Accurate flat components with clean edges and consistent dimensions.",
    pressBrakeBending: "Press Brake Bending",
    pressBrakeBendingDesc: "Flat sheets formed into functional brackets, channels, and covers.",
    tubePipeFab: "Tube & Pipe Fabrication",
    tubePipeFabDesc: "Round and square hollow sections into poles, stands, and barriers.",
    weldingHeavyFab: "Welding & Heavy Fabrication",
    weldingHeavyFabDesc: "Cutting, bending, welding into complete assemblies ready for install.",
    powderCoatingCap: "Powder Coating",
    powderCoatingCapDesc: "Durable clean surface finish for steel and metal components.",
    epoxyCoatingCap: "Epoxy Coating",
    epoxyCoatingCapDesc: "Enhanced protection for demanding environments and harsh conditions.",
    projectSupportCap: "Project Support",
    projectSupportCapDesc: "Review BOQs, drawings, and material lists to coordinate solutions.",
    needCustom: "Need a Custom Solution?",
    needCustomDesc: "Contact Sky Mark Co. to discuss your specific industrial supply, fabrication, or coating requirements.",

    // Products page
    productsTitle: "Products & Solutions",
    productsSubtitle: "A practical range of industrial supply, CNC fabrication, steel fabrication, and coating solutions for contractors and project teams.",
    browseByCategory: "BROWSE BY CATEGORY",
    twelveCategories: "12 Product & Service Categories",
    quickReference: "QUICK REFERENCE",
    typicalOutputs: "Typical Outputs by Capability",
    needSpecific: "Need a Specific Product or Fabrication Item?",
    needSpecificDesc: "Contact our team for a tailored solution based on your project requirements.",

    // CNC page
    cncTitle: "CNC & Fabrication Solutions",
    cncSubtitle: "Laser cutting, bending, tube fabrication, welding, and project-based steel manufacturing support.",
    cncLaserCutComponents: "CNC Laser Cut Components",
    cncLaserCutDesc: "Precision flat steel components cut to exact specifications.",
    pressBrakeFormed: "Press Brake Formed Components",
    pressBrakeFormedDesc: "Bent sheet metal shapes for structural support applications.",
    tubeFabSection: "Tube & Pipe Fabrication",
    tubeFabSectionDesc: "Poles, stands, frames, arms, rails, and barriers from tubular steel.",
    standardParts: "Standard Parts Library",
    standardPartsDesc: "Common fabrication items in various sizes and finishes.",
    customPart: "Have a Custom Part?",
    customPartDesc: "Share your drawing, BOQ, or sample reference with us for a tailored fabrication solution.",
    sendCustomInquiry: "Send Custom Fabrication Inquiry",

    // Steel page
    steelTitle: "Steel Fabrication",
    steelSubtitle: "Custom fabricated steel components for project, infrastructure, and industrial requirements.",
    heavyFab: "Heavy Fabrication & Structural Assemblies",
    heavyFabDesc: "Larger welded steel items and assemblies used in projects.",
    elecInstSupports: "Electrical & Instrumentation Supports",
    elecInstSupportsDesc: "Fabricated steel items used to hold electrical and instrumentation equipment.",
    stainlessSteel: "Stainless Steel Fabrication",
    stainlessSteelDesc: "Corrosion-resistant components for demanding environments.",
    customProject: "Custom Project Manufacturing",
    customProjectDesc: "Drawing-based and BOQ-based custom fabrication.",
    inputsAccepted: "Inputs Accepted",
    needSteel: "Need Custom Steel Fabrication?",
    needSteelDesc: "Share your drawings, BOQ, or requirements with our team for a tailored solution.",

    // Coating page
    coatingTitle: "Powder Coating & Epoxy Coating",
    coatingSubtitle: "Protective and clean finishing solutions for fabricated steel and industrial components.",
    powderCoating: "Powder Coating",
    powderCoatingDesc: "Dry finishing process for durable, clean, consistent surface finish.",
    epoxyCoating: "Epoxy Coating",
    epoxyCoatingDesc: "Protective surface layer for enhanced resistance and durability.",
    benefits: "Benefits",
    applications: "APPLICATIONS",
    coatingApplications: "Coating Applications",
    needCoating: "Need Coating for Fabricated or Industrial Components?",
    needCoatingDesc: "Contact Sky Mark Co. for powder coating, epoxy coating, and protective finishing solutions.",
    sendCoatingInquiry: "Send Coating Inquiry",

    // Catalogs page
    catalogsTitle: "Catalogs & Downloads",
    catalogsSubtitle: "Download Sky Mark Co. profiles and catalogs for industrial supply, CNC fabrication, steel fabrication, coating, and project-related materials.",
    downloads: "DOWNLOADS",
    companyCatalogs: "Company Catalogs & Profiles",
    catalogsDesc: "Download our latest catalogs and company profiles. If a PDF is not yet available, please contact us to request the latest copy.",
    downloadPDF: "Download PDF",
    comingSoon: "Coming Soon",

    // Gallery page
    galleryTitle: "Gallery",
    gallerySubtitle: "Selected visuals from Sky Mark Co. workshop, fabrication activities, coating work, material supply, deliveries, and team operations.",

    // Contact page
    contactTitle: "Contact Sky Mark Co.",
    contactSubtitle: "Industrial Supply, Steel Fabrication & Coating Solutions",
    headOffice: "Head Office",
    shortAddress: "Short Address",
    contactPerson: "Contact Person",
    mobileWhatsapp: "Mobile / WhatsApp",
    email: "Email",
    businessHours: "Business Hours",
    openLocation: "Open Location in Google Maps",
    sendInquiryForm: "Send an Inquiry",
    fillForm: "Fill in the form below and our team will get back to you.",
    fullName: "Full Name",
    fullNamePlaceholder: "Your full name",
    companyName: "Company Name",
    companyPlaceholder: "Your company",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    mobileLabel: "Mobile Number",
    mobilePlaceholder: "+966 XX XXX XXXX",
    inquiryType: "Inquiry Type",
    selectInquiry: "Select inquiry type",
    message: "Message",
    messagePlaceholder: "Describe your requirement...",
    sending: "Sending...",
    sendInquiryBtn: "Send Inquiry",
    thankYou: "Thank You",
    inquiryReceived: "Thank you for contacting Sky Mark Co. Your inquiry has been received successfully. Our team will review your message and respond as soon as possible.",
    somethingWrong: "Something went wrong. Please try again or contact us directly via WhatsApp.",
    preferWhatsapp: "Prefer instant messaging? Reach us on WhatsApp.",
    quotationRequest: "Quotation Request",
    vendorRegistration: "Vendor Registration",
    industrialSupplyOpt: "Industrial Supply",
    electricalSupplyOpt: "Electrical Supply",
    mechanicalSupplyOpt: "Mechanical Supply",
    civilMaterialsOpt: "Civil Materials",
    cncLaserOpt: "CNC Laser Cutting",
    pressBrakeOpt: "Press Brake Bending",
    tubeFabOpt: "Tube Fabrication",
    steelFabOpt: "Steel Fabrication",
    powderCoatingOpt: "Powder Coating",
    epoxyCoatingOpt: "Epoxy Coating",
    customFabOpt: "Custom Fabrication",
    generalInquiry: "General Inquiry",

    // Footer
    getInTouch: "Get in Touch",
    quickLinks: "Quick Links",
    copyright: "All rights reserved.",

    // Form validation
    isRequired: "is required",
    validEmail: "Please enter a valid email",
    fridayClosed: "Friday: Closed",

    // Form validation

    // Chairman
    chairmanMessage: "Chairman's Message",
    chairmanName: "Saleh Masoud Saeed Al Zahrani (Abu Waleed)",
    chairmanTitle: "Chairman, Sky Mark Co.",
    chairmanGreeting: "Dear Valued Partners and Clients,",
    chairmanP1: "Welcome to Sky Mark Co. It is my privilege to lead a company that stands at the intersection of industrial innovation and reliable service. Since our establishment in Riyadh, we have been driven by a singular purpose: to deliver precision-engineered solutions that power Saudi Arabia's infrastructure and industrial growth.",
    chairmanP2: "At Sky Mark Co., we believe that every project — whether a small custom fabrication or a large-scale supply coordination — deserves the same level of commitment, quality, and attention to detail. Our team works tirelessly to ensure that our clients receive not just products, but complete solutions tailored to their exact requirements.",
    chairmanP3: "As Saudi Arabia continues its remarkable journey of development under Vision 2030, we remain dedicated to being a trusted local partner for contractors, project engineers, and industrial clients across the Kingdom. Thank you for choosing Sky Mark Co. We look forward to building lasting partnerships and contributing to the success of your projects.",
    chairmanClosing: "With sincere regards,",

    // Language
    language: "Language",
    english: "English",
    arabic: "العربية",
  },
  ar: {
    // Nav
    home: "الرئيسية",
    about: "من نحن",
    capabilities: "القدرات",
    products: "المنتجات والحلول",
    cnc: "التصنيع باستخدام الحاسب الآلي",
    steel: "تصنيع الفولاذ",
    coating: "الطلاء",
    catalogs: "الكتالوجات والتنزيلات",
    gallery: "المعرض",
    contact: "اتصل بنا",
    sendInquiry: "إرسال استفسار",
    whatsapp: "واتساب",
    whatsappUs: "تواصل عبر واتساب",

    // Hero
    heroLocation: "الرياض، المملكة العربية السعودية",
    heroTitleLine1: "التوريد",
    heroTitleLine2: "الصناعي",
    heroTitleLine3: "والتصنيع",
    heroSubtitle: "قطع بالليزر CNC، تصنيع الفولاذ، ثني الصفائح، تصنيع الأنابيب، طلاء مسحوقي، وتوريد المشاريع من الرياض.",
    exploreCapabilities: "استكشف القدرات",
    viewCategories: "عرض فئات المنتجات",
    whatsappInquiry: "استفسار واتساب",

    // Stats
    coreServices: "الخدمات",
    dayWorkWeek: "أيام العمل",
    projectFocused: "تركيز على المشاريع",
    riyadhBase: "مقر الرياض",
    serviceAreas: "مجالات الخدمة",

    // About section
    aboutOverline: "من نحن",
    aboutTitle: "شريك صناعي عملي لمشاريعك",
    aboutP1: "شركة سكاي مارك هي شركة مقرها الرياض تخدم المقاولين ومشاريع البنية التحتية والعملاء الصناعيين في جميع أنحاء المملكة العربية السعودية بالتوريد والتصنيع CNC وتصنيع الفولاذ وحلول الطلاء.",
    industrialSupply: "التوريد الصناعي",
    industrialSupplyShort: "كهربائي، ميكانيكي، مدني",
    cncFab: "التصنيع CNC",
    cncFabShort: "ليزر، ثني، أنابيب، لحام",
    projectReady: "جاهز للمشروع",
    boqDrawing: "بناءً على BOQ والرسومات",
    learnMore: "تعرف أكثر علينا",

    // Services
    whatWeDo: "ما نقدمه",
    coreServicesTitle: "الخدمات الأساسية",

    // Products
    productScope: "نطاق المنتجات",
    productCategoriesTitle: "فئات المنتجات ونطاق التصنيع",
    viewAll: "عرض جميع الفئات",

    // Why Choose
    whySkyMark: "لماذا سكاي مارك؟",
    builtForProjects: "مبنية للمشاريع.",
    trustedByIndustry: "موثوقة من الصناعة.",
    whyDescription: "نحن نجمع بين التوريد الصناعي والتصنيع CNC وتصنيع الفولاذ والطلاء تحت نقطة اتصال واحدة — مما يقلل من عبء التنسيق لفرق المشاريع.",

    // Process
    howWeWork: "كيف نعمل",
    fromInquiryToDelivery: "من الاستفسار إلى التسليم",
    step1Title: "شارك المتطلبات",
    step1Desc: "BOQ، رسومات، مخططات، أو قائمة مواد",
    step2Title: "المراجعة والتوضيح",
    step2Desc: "النطاق، المادة، التشطيب، الكمية، الجدول الزمني",
    step3Title: "عرض السعر",
    step3Desc: "عرض السعر + تنسيق التوريد والتسليم",
    step4Title: "الإنتاج",
    step4Desc: "القطع، الثني، التصنيع، الطلاء",
    step5Title: "الفحص والتسليم",
    step5Desc: "تم الفحص والتغليف والتسليم للمشروع",

    // CTA
    readyToDiscuss: "هل أنت جاهز لمناقشة متطلباتك؟",
    ctaDescription: "اتصل بشركة سكاي مارك للتوريد الصناعي والتصنيع CNC وتصنيع الفولاذ والطلاء المسحوقي والطلاء الإيبوكسي ومتطلبات المواد المتعلقة بالمشاريع.",
    callWhatsapp: "اتصال / واتساب",
    viewLocation: "عرض الموقع",

    // Trust strip
    riyadhBased: "مقر الرياض",
    riyadhBasedDesc: "تواصل محلي وتنسيق سريع",
    multiScope: "نطاق واسع",
    multiScopeDesc: "كهربائي، ميكانيكي، مدني وأكثر",
    fabrication: "التصنيع",
    fabricationDesc: "ليزر CNC، ثني، لحام، أنابيب",
    directContact: "تواصل مباشر",
    directContactDesc: "واتساب، جوال، بريد، استمارة",

    // Service short descriptions
    electricalSupplyShort: "Electrical accessories & install",
    mechanicalSupplyShort: "Supports & accessories",
    civilMaterialsDesc: "Construction & infrastructure",
    steelFabDesc: "Custom assemblies",

    // About page
    aboutPageTitle: "عن سكاي مارك",
    aboutPageSubtitle: "حلول التوريد الصناعي والتصنيع والطلاء مقرها الرياض لمشاريع المقاولين في جميع أنحاء المملكة العربية السعودية.",
    ourCompany: "شركتنا",
    precisionPerformancePromise: "الدقة. الأداء. الوعد.",
    whatWeDoCard: "ما نقوم به",
    whatWeDoCardDesc: "التوريد الصناعي والتصنيع CNC وتصنيع الفولاذ وحلول الطلاء للمقاولين وفرق المشاريع في جميع أنحاء المملكة العربية السعودية.",
    howWeWorkCard: "كيف نعمل",
    howWeWorkCardDesc: "فهم المتطلب. تنسيق الحل الصحيح. الدعم حتى الإنجاز.",
    ourValues: "قيمنا",
    threePrinciples: "ثلاثة مبادئ توجهنا",
    precision: "الدقة",
    precisionDesc: "فهم واضح. نتائج دقيقة.",
    performance: "الأداء",
    performanceDesc: "استجابة سريعة. موثوق. في الوقت المحدد.",
    promise: "الوعد",
    promiseDesc: "موثوق. شفاف. ملتزم.",
    whatWeSupport: "ما ندعمه",
    ourMarketFocus: "تركيز السوق لدينا",
    infraConstruction: "البنية التحتية والإنشاءات",
    infraConstructionDesc: "المرافق، التطويرات التجارية، المنشآت الصناعية",
    contractorLed: "المشاريع بقيادة المقاولين",
    contractorLedDesc: "فرق EPC، إدارات المشتريات، مهندسي المشاريع",
    materialPackages: "حزم المواد المستندة للمشاريع",
    materialPackagesDesc: "تنسيق التوريد بناءً على BOQ والرسومات",
    workWithUs: "اعمل مع سكاي مارك",
    workWithUsDesc: "تواصل مع فريقنا لمناقشة متطلبات التوريد أو التصنيع أو الطلاء الخاصة بك.",

    // Capabilities page
    capabilitiesTitle: "القدرات",
    capabilitiesSubtitle: "التوريد الصناعي والتصنيع CNC وتصنيع الفولاذ والطلاء ودعم المشاريع من الرياض، المملكة العربية السعودية.",
    industrialSupplyCap: "التوريد الصناعي",
    industrialSupplyCapDesc: "مواد للمجالات الكهربائية والميكانيكية والمدنية والصناعية العامة.",
    cncLaserCutting: "قطع الليزر CNC",
    cncLaserCuttingDesc: "مكونات مسطحة دقيقة بأحافظ نظيفة وأبعاد متسقة.",
    pressBrakeBending: "ثني الصفائح",
    pressBrakeBendingDesc: "تحويل الصفائح المسطحة إلى أقواس وقنوات وأغطية وظيفية.",
    tubePipeFab: "تصنيع الأنابيب والمواسير",
    tubePipeFabDesc: "تحويل المقاطع المجوفة الدائرية والمربعة إلى أعمدة ومواقف وحواجز.",
    weldingHeavyFab: "اللحام والتصنيع الثقيل",
    weldingHeavyFabDesc: "قطع وثني ولحام مكونات متعددة إلى تجميعات كاملة جاهزة للتركيب.",
    powderCoatingCap: "الطلاء المسحوقي",
    powderCoatingCapDesc: "تشطيب سطحي نظيف ودائم لمكونات الفولاذ والمعادن.",
    epoxyCoatingCap: "الطلاء الإيبوكسي",
    epoxyCoatingCapDesc: "حماية معززة للبيئات الصعبة والظروف القاسية.",
    projectSupportCap: "دعم المشاريع",
    projectSupportCapDesc: "مراجعة قوائم BOQ والرسومات وقوائم المواد لتنسيق الحلول.",
    needCustom: "هل تحتاج حل مخصص؟",
    needCustomDesc: "اتصل بشركة سكاي مارك لمناقشة متطلبات التوريد أو التصنيع أو الطلاء الخاصة بك.",

    // Products page
    productsTitle: "المنتجات والحلول",
    productsSubtitle: "مجموعة عملية من حلول التوريد الصناعي والتصنيع CNC وتصنيع الفولاذ والطلاء للمقاولين وفرق المشاريع.",
    browseByCategory: "تصفح حسب الفئة",
    twelveCategories: "12 فئة للمنتجات والخدمات",
    quickReference: "مرجع سريع",
    typicalOutputs: "المخرجات النموذجية حسب القدرة",
    needSpecific: "هل تحتاج منتج أو مادة تصنيع محددة؟",
    needSpecificDesc: "تواصل مع فريقنا للحصول على حل مخصص بناءً على متطلبات مشروعك.",

    // CNC page
    cncTitle: "حلول التصنيع CNC",
    cncSubtitle: "قطع بالليزر، ثني، تصنيع الأنابيب، لحام، ودعم التصنيع الفولاذي المستند للمشاريع.",
    cncLaserCutComponents: "مكونات قطع الليزر CNC",
    cncLaserCutDesc: "مكونات فولاذية مسطحة دقيقة مقطوعة بمواصفات دقيقة.",
    pressBrakeFormed: "مكونات ثني الصفائح",
    pressBrakeFormedDesc: "أشكال معدنية مطوية لتطبيقات الدعم الهيكلي.",
    tubeFabSection: "تصنيع الأنابيب والمواسير",
    tubeFabSectionDesc: "أعمدة، مواقف، أطر، ذراع، قضبان، وحواجز من الفولاذ الأنبوبي.",
    standardParts: "مكتبة القطع القياسية",
    standardPartsDesc: "قطع تصنيع شائعة بأحجام وتشطيبات مختلفة.",
    customPart: "هل لديك جزء مخصص؟",
    customPartDesc: "شارك رسمك أو BOQ أو مرجع العينة معنا للحصول على حل تصنيع مخصص.",
    sendCustomInquiry: "إرسال استفسار تصنيع مخصص",

    // Steel page
    steelTitle: "تصنيع الفولاذ",
    steelSubtitle: "مكونات فولاذية مصنعة مخصصة للمشاريع والبنية التحتية والمتطلبات الصناعية.",
    heavyFab: "التصنيع الثقيل والتجميعات الهيكلية",
    heavyFabDesc: "عناصر فولاذية مُلحمة كبيرة وتجميعات مستخدمة في المشاريع.",
    elecInstSupports: "دعم الكهرباء والأجهزة",
    elecInstSupportsDesc: "عناصر فولاذية مصنعة لحمل صناديق الكهرباء والألواح والأجهزة والكابلات.",
    stainlessSteel: "تصنيع الفولاذ المقاوم للصدأ",
    stainlessSteelDesc: "مكونات مقاومة للتآكل للبيئات الصعبة.",
    customProject: "التصنيع المخصص للمشاريع",
    customProjectDesc: "تصنيع مخصص بناءً على الرسومات وقوائم BOQ.",
    inputsAccepted: "المدخلات المقبولة",
    needSteel: "هل تحتاج تصنيع فولاذي مخصص؟",
    needSteelDesc: "شارك رسوماتك أو BOQ أو متطلباتك مع فريقنا للحصول على حل مخصص.",

    // Coating page
    coatingTitle: "الطلاء المسحوقي والطلاء الإيبوكسي",
    coatingSubtitle: "حلول التشطيب الواقي والنظيف للفولاذ المصنع والمكونات الصناعية.",
    powderCoating: "الطلاء المسحوقي",
    powderCoatingDesc: "عملية تشطيب جافة لسطح دائم ونظيف ومتسق.",
    epoxyCoating: "الطلاء الإيبوكسي",
    epoxyCoatingDesc: "طبقة سطحية واقية لمقاومة معززة ومتانة أعلى.",
    benefits: "المنافع",
    applications: "التطبيقات",
    coatingApplications: "تطبيقات الطلاء",
    needCoating: "هل تحتاج طلاءً لمكوناتك الصناعية؟",
    needCoatingDesc: "اتصل بشركة سكاي مارك للطلاء المسحوقي والطلاء الإيبوكسي وحلول التشطيب الواقي.",
    sendCoatingInquiry: "إرسال استفسار طلاء",

    // Catalogs page
    catalogsTitle: "الكتالوجات والتنزيلات",
    catalogsSubtitle: "حمّل ملفات تعريف شركة سكاي مارك والكتالوجات للتوريد الصناعي والتصنيع CNC وتصنيع الفولاذ والطلاء.",
    downloads: "التنزيلات",
    companyCatalogs: "كتالوجات وملفات الشركة",
    catalogsDesc: "حمّل أحدث الكتالوجات وملفات الشركة. إذا لم يكن PDF متاحًا بعد، يرجى التواصل معنا لطلب النسخة الأحدث.",
    downloadPDF: "تحميل PDF",
    comingSoon: "قريباً",

    // Gallery page
    galleryTitle: "المعرض",
    gallerySubtitle: "صور مختارة من ورشة عمل سكاي مارك، أنشطة التصنيع، أعمال الطلاء، توريد المواد، التسليمات، وعمليات الفريق.",

    // Contact page
    contactTitle: "اتصل بشركة سكاي مارك",
    contactSubtitle: "التوريد الصناعي وتصنيع الفولاذ وحلول الطلاء",
    headOffice: "المكتب الرئيسي",
    shortAddress: "العنوان المختصر",
    contactPerson: "الشخص المسؤول",
    mobileWhatsapp: "الجوال / واتساب",
    email: "البريد الإلكتروني",
    businessHours: "ساعات العمل",
    openLocation: "فتح الموقع في Google Maps",
    sendInquiryForm: "إرسال استفسار",
    fillForm: "املأ النموذج أدناه وسيتواصل معك فريقنا.",
    fullName: "الاسم الكامل",
    fullNamePlaceholder: "اسمك الكامل",
    companyName: "اسم الشركة",
    companyPlaceholder: "شركتك",
    emailLabel: "البريد الإلكتروني",
    emailPlaceholder: "your@email.com",
    mobileLabel: "رقم الجوال",
    mobilePlaceholder: "+966 XX XXX XXXX",
    inquiryType: "نوع الاستفسار",
    selectInquiry: "اختر نوع الاستفسار",
    message: "الرسالة",
    messagePlaceholder: "صف متطلباتك...",
    sending: "جاري الإرسال...",
    sendInquiryBtn: "إرسال الاستفسار",
    thankYou: "شكراً لك",
    inquiryReceived: "شكراً لتواصلك مع شركة سكاي مارك. تم استلام استفسارك بنجاح. سيراجع فريقنا رسالتك وسيرد عليك في أقرب وقت ممكن.",
    somethingWrong: "حدث خطأ ما. يرجى المحاولة مرة أخرى أو التواصل معنا مباشرة عبر واتساب.",
    preferWhatsapp: "تفضل المراسلة الفورية؟ تواصل معنا عبر واتساب.",
    quotationRequest: "طلب عرض سعر",
    vendorRegistration: "تسجيل مورد",
    industrialSupplyOpt: "التوريد الصناعي",
    electricalSupplyOpt: "التوريد الكهربائي",
    mechanicalSupplyOpt: "التوريد الميكانيكي",
    civilMaterialsOpt: "المواد المدنية",
    cncLaserOpt: "قطع الليزر CNC",
    pressBrakeOpt: "ثني الصفائح",
    tubeFabOpt: "تصنيع الأنابيب",
    steelFabOpt: "تصنيع الفولاذ",
    powderCoatingOpt: "الطلاء المسحوقي",
    epoxyCoatingOpt: "الطلاء الإيبوكسي",
    customFabOpt: "تصنيع مخصص",
    generalInquiry: "استفسار عام",

    // Footer
    getInTouch: "تواصل معنا",
    quickLinks: "روابط سريعة",
    copyright: "جميع الحقوق محفوظة.",

    // Form validation
    isRequired: "مطلوب",
    validEmail: "يرجى إدخال بريد إلكتروني صحيح",
    fridayClosed: "الجمعة: مغلق",

    // Chairman
    chairmanMessage: "رسالة رئيس مجلس الإدارة",
    chairmanName: "صالح مسعود سعيد الزهراني (أبو وليد)",
    chairmanTitle: "رئيس مجلس الإدارة، سكاي مارك",
    chairmanGreeting: "شركاؤنا وعملاؤنا الكرام،",
    chairmanP1: "أهلاً بكم في سكاي مارك. إنه لشرف لي أن أقود شركة تقف عند تقاطع الابتكار الصناعي والخدمة الموثوقة. منذ تأسيسنا في الرياض، كنا مدفوعين بهدف واحد: تقديم حلول هندسية دقيقة تدعم نمو البنية التحتية والصناعة في المملكة العربية السعودية.",
    chairmanP2: "في سكاي مارك، نؤمن بأن كل مشروع — سواء كان تصنيع مخصص صغير أو تنسيق توريد واسع النطاق — يستحق نفس المستوى من الالتزام والجودة والاهتمام بالتفاصيل. يعمل فريقنا بلا كلل لضمان حصول عملائنا ليس فقط على منتجات، بل على حلول كاملة مصممة خصيصاً لتلبية متطلباتهم بالضبط.",
    chairmanP3: "مع استمرار المملكة العربية السعودية في رحلتها المتميزة للتنمية ضمن رؤية 2030، نبقى ملتزمين بأن نكون شريكاً محلياً موثوقاً للمقاولين ومهندسي المشاريع والعملاء الصناعيين في جميع أنحاء المملكة. شكراً لاختياركم سكاي مارك. نتطلع إلى بناء شراكات دائمة والمساهمة في نجاح مشاريعكم.",
    chairmanClosing: "مع خالص التقدير،",

    // Language
    language: "اللغة",
    english: "English",
    arabic: "العربية",
  },
} as const;

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("skymark-lang") as Language) || "en";
    }
    return "en";
  });

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
    localStorage.setItem("skymark-lang", newLang);
  }, []);

  const toggle = useCallback(() => {
    setLangState((prev) => {
      const next = prev === "en" ? "ar" : "en";
      localStorage.setItem("skymark-lang", next);
      return next;
    });
  }, []);

  const isRTL = lang === "ar";

  const t = useCallback(
    (key: string) => {
      return translations[lang][key] || translations.en[key];
    },
    [lang]
  );

  // Update html dir and lang attributes
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.body.dir = isRTL ? "rtl" : "ltr";
  }, [lang, isRTL]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
